import React, { useState, useEffect, useRef } from 'react';
import { GraduationCap, Check, BookOpen, Award, Target, Users, Zap } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AcademyContent from './hero/mockup/content/AcademyContent';

const EditorialAcademySection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Match Academy page theming (this page uses ThemeContext, not next-themes)
  const bgColor = isDark ? '#111111' : '#FFFFFF';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const featureTextColor = isDark ? '#DDDDDD' : '#333333';

  const badgeBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const badgeIconColor = isDark ? '#AAAAAA' : '#555555';
  const badgeTextColor = isDark ? '#FFFFFF' : '#111111';
  const badgeGradient = isDark
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';

  const mockupBg = isDark ? '#1A1A1A' : '#F8F8F8';
  const mockupBorder = isDark ? '#333333' : '#E5E5E5';

  const features = [
    { text: "100% Free Access", icon: Award },
    { text: "Step-by-Step Courses", icon: BookOpen },
    { text: "Expert-Led Training", icon: Users },
    { text: "Certification Tracks", icon: Target },
    { text: "Community Support", icon: Zap }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-6" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge with animated border - V3 style */}
            <div className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full overflow-hidden mb-6">
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: badgeGradient,
                  backgroundSize: '200% 100%',
                  animation: 'borderTrace 2s linear infinite',
                }}
              />
              <div 
                className="absolute inset-0 rounded-full"
                style={{ border: `1px solid ${badgeBorderColor}` }}
              />
              <div 
                className="absolute inset-[1px] rounded-full"
                style={{ backgroundColor: badgeBg }}
              />
              <GraduationCap className="relative z-10 w-3 h-3" style={{ color: badgeIconColor }} />
              <span className="relative z-10 text-xs font-medium" style={{ color: badgeTextColor }}>Academy</span>
            </div>

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: headingColor }}
            >
              Your Free Marketing Education.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: textColor }}
            >
              Master modern marketing with expert-led courses, step-by-step tutorials, and certification tracks—completely free.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: isDark ? 'rgba(16,185,129,0.18)' : '#D1FAE5' }}
                  >
                    <Check
                      className="w-3 h-3"
                      style={{ color: isDark ? '#34D399' : '#059669' }}
                    />
                  </div>
                  <span className="text-sm" style={{ color: featureTextColor }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <span 
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg opacity-60 cursor-not-allowed"
              style={{ backgroundColor: isDark ? '#FFFFFF' : '#000000', color: isDark ? '#000000' : '#FFFFFF' }}
            >
              Launching Soon
            </span>
          </div>

          {/* Right Visual - Academy Dashboard Mockup */}
          <div className="relative">
            <div 
              className="rounded-xl overflow-hidden"
              style={{ 
                backgroundColor: mockupBg, 
                border: `1px solid ${mockupBorder}`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                transition: 'all 0.6s ease-out',
                height: '340px'
              }}
            >
              {isVisible && <AcademyContent animationDelay={300} />}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes borderTrace {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
};

export default EditorialAcademySection;
